import './SourceSelector.css';

import { ReactComponent as GithubIcon } from '../../assets/images/github-logo.svg';
// `docker-logo.svg`, not the one behind DOCKER_LOGO.Logo: that asset
// (`stacks/docker.svg`) declares no `fill`, so it paints black whatever
// `color` says. Only the brand hex is borrowed from the shared constant.
import { ReactComponent as DockerIcon } from '../../assets/images/docker-logo.svg';
import { DOCKER_LOGO } from '../BuildMethodSelector/stackLogos';
import { CheckIcon, UploadIcon } from '../ds/icons';

export type AppSource = 'GITHUB' | 'DOCKER_HUB' | 'UPLOAD';

interface SourceSelectorProps {
  value: AppSource;
  onChange: (value: AppSource) => void;
  /** Shown on the GitHub card when the account already has an installation.
   * Absent means we say nothing rather than implying it is not connected. */
  githubConnected?: boolean;
}

const SOURCES: {
  source: AppSource;
  id: string;
  title: string;
  subtitle: string;
}[] = [
  {
    source: 'GITHUB',
    id: 'source-tab-github',
    title: 'GitHub repository',
    subtitle: 'Build from your source code',
  },
  {
    source: 'UPLOAD',
    id: 'source-tab-upload',
    // The code comes straight from this computer: no repository, no account
    // to connect. Built the same way a repository is.
    title: 'Upload files',
    subtitle: 'Drag a folder from your computer',
  },
  {
    source: 'DOCKER_HUB',
    id: 'source-tab-image',
    // Named for the provider, like the card beside it — "Container image" said
    // what the thing IS, which is the one question nobody arrives with. The
    // provider is the answer to "where is my app?", and it is also the honest
    // scope: the panel behind this card searches Docker Hub, reads Docker Hub's
    // rate limit, and takes Docker Hub credentials. Nothing else is accepted,
    // so nothing else is named.
    title: 'Docker Hub image',
    subtitle: 'Deploy a prebuilt image, public or private',
  },
];

export const SOURCE_TAB_ID: Record<AppSource, string> = Object.fromEntries(
  SOURCES.map((option) => [option.source, option.id])
) as Record<AppSource, string>;

// Where the app's code comes from. Asked once, at creation: switching an
// existing app's source is a different question with different consequences,
// and Settings does not offer it.
//
// Cards rather than the tab bar this used to be. A tab bar was the right shape
// when the two sources were interchangeable views of one list; they are not —
// they build differently, cost differently, and one of them auto-deploys on
// push and the other cannot. The card is what has room to say that before the
// choice is made, which a one-word tab never did.
//
// Still tab SEMANTICS, though: the body below renders only the active source,
// which is what `role="tab"` and `aria-controls` describe. The ids are the ones
// the panel already points at — changing them would silently break the label
// association, which no visual review would catch.
const SourceSelector = ({
  value,
  onChange,
  githubConnected,
}: SourceSelectorProps) => (
  <div
    className="grid grid-cols-3 gap-4 [@media(max-width:1023.98px)]:grid-cols-1"
    role="tablist"
    aria-label="App source"
  >
    {SOURCES.map((option) => {
      const selected = value === option.source;
      const isGithub = option.source === 'GITHUB';
      const isUpload = option.source === 'UPLOAD';

      return (
        <button
          key={option.source}
          type="button"
          id={option.id}
          role="tab"
          aria-selected={selected}
          aria-controls="source-panel"
          tabIndex={selected ? 0 : -1}
          onClick={() => onChange(option.source)}
          className={`b4-focus flex items-center gap-3.5 text-left px-4 py-3.5 rounded-card border transition-colors duration-[120ms] ${
            selected
              ? 'border-action bg-[#152B45]'
              : 'border-navy-500 hover:bg-navy-800'
          }`}
        >
          {/* The logo, not a box holding the logo. Filling both cards said
              nothing about which one is picked; now the fill is what means
              "this one", and the tile that used to frame the icon was a third
              tint inside a card that is already a choice. */}
          <span className={`w-9 flex-none flex items-center justify-center ${selected ? 'text-white' : 'text-navy-200'}`}>
            {isUpload ? (
              <UploadIcon size={18} className="flex-none" />
            ) : isGithub ? (
              // Connected is a dot on the logo, not a badge beside the text:
              // with three cards in a row the badge took the width the title
              // needed and cut it to "GitHub repos…".
              <span
                className="relative flex-none inline-flex"
                title={githubConnected ? 'GitHub connected' : undefined}
              >
                <GithubIcon width="18px" height="18px" className="flex-none" />
                {githubConnected ? (
                  <>
                    <span
                      aria-hidden="true"
                      className="absolute -right-[3px] -bottom-[3px] w-2 h-2 rounded-full bg-state-ok ring-2 ring-navy-800"
                    />
                    <span className="sr-only">GitHub connected</span>
                  </>
                ) : null}
              </span>
            ) : (
              <DockerIcon
                width="18px"
                height="18px"
                className="flex-none"
                // Docker's own blue, from the shared brand constant the build
                // method selector already uses — the whale in the platform's
                // grey read as a disabled control sitting next to an enabled
                // one.
                style={{ color: DOCKER_LOGO.color }}
              />
            )}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-ds-135 font-semibold leading-[1.4] text-white truncate">
              {option.title}
            </span>
            {/* Wraps rather than truncates. "auto-deploy on push" is the whole
                difference between the two sources, and it is the half that
                falls off the end — a card that cuts its own explanation is
                worse than a card two lines tall. The grid stretches both to
                the same height, so wrapping costs nothing. */}
            {/* Not on a phone: stacked, the two cards already took a screen of
                their own, and the title says which is which. */}
            <span className="hidden sm:block font-courrier text-ds-115 leading-[1.4] mt-1 text-navy-200">
              {option.subtitle}
            </span>
          </span>
          <span className="flex items-center gap-2.5 flex-none">
            {selected ? (
              <span className="w-5 h-5 flex-none rounded-full bg-action text-white flex items-center justify-center">
                <CheckIcon size={13} />
              </span>
            ) : (
              <span className="w-5 h-5 flex-none rounded-full border border-navy-400" />
            )}
          </span>
        </button>
      );
    })}
  </div>
);

export default SourceSelector;
